import { Link } from "react-router-dom";
import { LucideBookOpen, LucidePencil, LucideEye, LucideMoreHorizontal } from "lucide-react";
import { getTypeLabelByValue } from "@/constants/reportTypes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const SIZES = {
  lg: {
    outer:       "w-[380px]",
    row:         "px-5 py-4 gap-4",
    badge:       "min-w-[60px] px-3 py-2",
    badgeLabel:  "text-[8px]",
    badgeNum:    "text-3xl",
    iconSize:    20,
    name:        "text-base",
    reportBadge: "text-[11px] px-2 py-0.5 mt-1.5",
    footer:      "px-5 py-2",
    footerText:  "text-[13px]",
    permIcon:    16,
    triggerSize: 32,
  },
  md: {
    outer:       "w-[260px]",
    row:         "px-4 py-3 gap-3",
    badge:       "min-w-[50px] px-2.5 py-1.5",
    badgeLabel:  "text-[7px]",
    badgeNum:    "text-2xl",
    iconSize:    16,
    name:        "text-sm",
    reportBadge: "text-[10px] px-1.5 py-0.5 mt-1",
    footer:      "px-4 py-1.5",
    footerText:  "text-[11px]",
    permIcon:    14,
    triggerSize: 28,
  },
};

function PermissionIcon({ permission, size }) {
  if (!permission) return null;
  const Icon = permission === "edit" ? LucidePencil : LucideEye;
  return (
    <div className="border border-neutral-200 dark:border-white/25 rounded-md p-1.5 bg-neutral-100 dark:bg-white/10">
      <Icon size={size} className="text-neutral-400 dark:text-white/60" />
    </div>
  );
}

function ActionsMenu({ menuItems, triggerSize }) {
  if (!menuItems?.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          style={{ width: triggerSize, height: triggerSize }}
          className="p-0 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-white/80 dark:hover:text-white dark:hover:bg-white/15 rounded-lg transition-colors"
        >
          <LucideMoreHorizontal size={triggerSize - 12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {menuItems.map(({ label, icon: Icon, onClick, destructive }) => (
          <DropdownMenuItem
            key={label}
            onClick={onClick}
            className={destructive ? "text-destructive focus:text-destructive focus:bg-destructive/10" : ""}
          >
            {Icon && <Icon size={14} className="mr-2" />}
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HorizontalCard({ item, menuItems, ownerName, permission, cfg, reportLabel }) {
  return (
    <div className="bg-white from-brand-700 to-brand-800 dark:bg-gradient-to-r flex flex-col">
      <div className={`flex items-center ${cfg.row}`}>
        {item.grade ? (
          <div className={`bg-brand-50 border border-brand-200 dark:bg-white/95 dark:border-transparent rounded-xl text-center shrink-0 shadow-sm ${cfg.badge}`}>
            <p className={`font-bold uppercase tracking-widest text-brand-600 dark:text-neutral-400 leading-none mb-1 ${cfg.badgeLabel}`}>
              Grade
            </p>
            <p className={`font-bold text-brand-700 dark:text-neutral-900 leading-none ${cfg.badgeNum}`}>
              {item.grade}
            </p>
          </div>
        ) : (
          <div className="bg-brand-50 dark:bg-white/20 rounded-xl p-2.5 shrink-0">
            <LucideBookOpen size={cfg.iconSize} className="text-brand-600 dark:text-white" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-neutral-900 dark:text-white leading-snug truncate ${cfg.name}`}>
            {item.name || "Unnamed class"}
          </p>
          {reportLabel && (
            <span className={`inline-block font-medium text-neutral-500 dark:text-white/70 bg-neutral-100 dark:bg-white/10 border border-neutral-200 dark:border-white/20 rounded-md ${cfg.reportBadge}`}>
              {reportLabel}
            </span>
          )}
          {item.schoolYear && (
            <p className="text-[11px] text-neutral-400 dark:text-white/50 font-normal mt-0.5 truncate">
              {item.schoolYear}
            </p>
          )}
        </div>

        <div
          className="shrink-0 z-10 flex flex-col items-center gap-1"
          onClick={(e) => e.preventDefault()}
        >
          <ActionsMenu menuItems={menuItems} triggerSize={cfg.triggerSize} />
          <PermissionIcon permission={permission} size={cfg.permIcon} />
        </div>
      </div>

      {ownerName && (
        <div className={`bg-neutral-50 border-t border-neutral-200 dark:bg-black/20 dark:border-t-transparent text-center ${cfg.footer}`}>
          <span className={`text-neutral-500 dark:text-white/70 font-medium ${cfg.footerText}`}>
            {ownerName}
          </span>
        </div>
      )}
    </div>
  );
}

function SmallCard({ item, menuItems, ownerName, permission, reportLabel }) {
  return (
    <div className="bg-white from-brand-700 to-brand-800 dark:bg-gradient-to-b flex flex-col">
      <div
        className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        onClick={(e) => e.preventDefault()}
      >
        <ActionsMenu menuItems={menuItems} triggerSize={28} />
      </div>

      <div className="flex flex-col items-center text-center px-4 pt-5 pb-4 gap-2">
        {item.grade ? (
          <div className="bg-brand-50 border border-brand-200 dark:bg-white/95 dark:border-transparent rounded-xl px-3 py-2 text-center shadow-sm min-w-[52px]">
            <p className="text-[7px] font-bold uppercase tracking-widest text-brand-600 dark:text-neutral-400 leading-none mb-1">
              Grade
            </p>
            <p className="text-2xl font-bold text-brand-700 dark:text-neutral-900 leading-none">
              {item.grade}
            </p>
          </div>
        ) : (
          <div className="bg-brand-50 dark:bg-white/20 rounded-xl p-2.5">
            <LucideBookOpen size={16} className="text-brand-600 dark:text-white" />
          </div>
        )}

        <p className="text-sm font-semibold text-neutral-900 dark:text-white leading-snug line-clamp-2">
          {item.name || "Unnamed class"}
        </p>

        {reportLabel && (
          <span className="text-[10px] font-medium text-neutral-500 dark:text-white/70 bg-neutral-100 dark:bg-white/10 border border-neutral-200 dark:border-white/20 rounded-md px-2 py-0.5">
            {reportLabel}
          </span>
        )}

        {item.schoolYear && (
          <p className="text-[10px] text-neutral-400 dark:text-white/45 font-normal">
            {item.schoolYear}
          </p>
        )}

        <PermissionIcon permission={permission} size={15} />
      </div>

      {ownerName && (
        <div className="bg-neutral-50 border-t border-neutral-200 dark:bg-black/20 dark:border-t-transparent px-3 py-1.5 text-center">
          <span className="text-[10px] text-neutral-500 dark:text-white/70 font-medium truncate">
            {ownerName}
          </span>
        </div>
      )}
    </div>
  );
}

export function ClassCard({ item, getLinkPath, getMenuItems, ownerName, permission, size = "lg" }) {
  const reportLabel = item.reportType ? getTypeLabelByValue(item.reportType) : null;
  const cfg = SIZES[size];
  const menuItems = getMenuItems?.(item) ?? [];

  return (
    <div className={`relative group rounded-2xl overflow-hidden shadow-sm hover:shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:-translate-y-px hover:border-primary transition-[border-color,box-shadow,transform] duration-150 border dark:border-transparent ${size === "sm" ? "w-[160px]" : cfg.outer}`}>
      <Link to={getLinkPath(item)} className="block">
        {size === "sm" ? (
          <SmallCard item={item} menuItems={menuItems} ownerName={ownerName} permission={permission} reportLabel={reportLabel} />
        ) : (
          <HorizontalCard item={item} menuItems={menuItems} ownerName={ownerName} permission={permission} cfg={cfg} reportLabel={reportLabel} />
        )}
      </Link>
    </div>
  );
}
